<template>
    <div class="pitch-debug">
        <h1>Pitch Detection Debug</h1>

        <div class="controls">
            <v-btn
                @click="toggleDetection"
                :disabled="!isSupported"
                class="text-primary"
            >
                {{ isActive ? "Stop" : "Start" }} Detection
            </v-btn>
            <span v-if="!isSupported" class="error">
                Microphone not supported in this browser
            </span>
        </div>

        <div v-if="error" class="error">
            {{ error }}
        </div>

        <div class="status text-high-emphasis">
            <strong>Status:</strong>
            {{ statusText }}
        </div>

        <div v-if="isActive" class="results">
            <div class="result-item">
                <strong>Frequency:</strong>
                {{ currentResult?.frequency?.toFixed(2) ?? "---" }} Hz
            </div>

            <div class="result-item">
                <strong>Note:</strong>
                {{ currentResult?.note ?? "---" }}
            </div>

            <div class="result-item">
                <strong>Cents Off:</strong>
                {{ currentResult?.centsOff?.toFixed(1) ?? "---" }}
            </div>

            <div class="result-item">
                <strong>Confidence:</strong>
                {{
                    currentResult
                        ? (currentResult.confidence * 100).toFixed(1)
                        : "---"
                }}%
            </div>

            <div class="result-item">
                <strong>Detector:</strong>
                {{ detectorName }}
            </div>
        </div>

        <v-card class="raw-data">
            <v-card-title> Raw Result </v-card-title>
            <v-card-text>
                <pre>{{ JSON.stringify(currentResult, null, 2) }}</pre>
            </v-card-text>
        </v-card>

        <div class="history">
            <h3>Detection History (last 10)</h3>
            <div
                v-for="(item, index) in history"
                :key="index"
                class="history-item"
            >
                <span class="timestamp">{{ item.timestamp }}</span>
                <span v-if="item.result.frequency">
                    {{ item.result.note }} ({{
                        item.result.frequency.toFixed(2)
                    }}Hz) {{ item.result.centsOff?.toFixed(1) }}¢
                </span>
                <span v-else class="no-pitch">No pitch detected</span>
            </div>
        </div>

        <!-- API Documentation -->
        <v-card class="api-section">
            <v-card-title class="text-h5">
                Pitch Detection API Documentation
            </v-card-title>
            <v-card-text>
                <h3 class="text-h6 mb-3">createPitchDetector()</h3>
                <pre class="code-block"><code>import { createPitchDetector, DetectorType } from '@/pitch';

const detector = createPitchDetector(
  DetectorType.BLUESHARP,  // Detector type
  'flute'                  // Instrument preset (optional)
);</code></pre>

                <h3 class="text-h6 mt-6 mb-3">PitchDetector Interface</h3>
                <v-table>
                    <thead>
                        <tr>
                            <th>Method</th>
                            <th>Parameters</th>
                            <th>Returns</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><code>detectPitch()</code></td>
                            <td>audioData: Float32Array, sampleRate: number</td>
                            <td>PitchDetectionResult</td>
                            <td>Detect pitch from audio buffer</td>
                        </tr>
                        <tr>
                            <td><code>setFrequencyRange()</code></td>
                            <td>min: number, max: number</td>
                            <td>void</td>
                            <td>Set frequency detection range in Hz</td>
                        </tr>
                        <tr>
                            <td><code>setConfidenceThreshold()</code></td>
                            <td>threshold: number (0-1)</td>
                            <td>void</td>
                            <td>Set minimum confidence for valid detection</td>
                        </tr>
                        <tr>
                            <td><code>getName()</code></td>
                            <td>-</td>
                            <td>string</td>
                            <td>Get detector implementation name</td>
                        </tr>
                    </tbody>
                </v-table>

                <h3 class="text-h6 mt-6 mb-3">PitchDetectionResult</h3>
                <pre class="code-block"><code>{
  frequency: number | null,  // Detected frequency in Hz
  confidence: number,        // Confidence score 0-1
  note?: string,            // Note name (e.g., "C5")
  centsOff?: number         // Cents deviation (+/-)
}</code></pre>

                <h3 class="text-h6 mt-6 mb-3">Instrument Presets</h3>
                <v-table>
                    <thead>
                        <tr>
                            <th>Preset</th>
                            <th>Frequency Range</th>
                            <th>Confidence Threshold</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><code>flute</code></td>
                            <td>250 - 2200 Hz</td>
                            <td>0.85</td>
                        </tr>
                        <tr>
                            <td><code>voice</code></td>
                            <td>80 - 1000 Hz</td>
                            <td>0.80</td>
                        </tr>
                        <tr>
                            <td><code>guitar</code></td>
                            <td>80 - 1500 Hz</td>
                            <td>0.85</td>
                        </tr>
                    </tbody>
                </v-table>

                <h3 class="text-h6 mt-6 mb-3">Utility Functions</h3>
                <ul class="api-list">
                    <li><code>frequencyToNoteName(frequency: number): string</code> - Convert Hz to note name</li>
                    <li><code>getCentsOffPitch(frequency: number): number</code> - Get cents deviation from nearest note</li>
                    <li><code>analyzeFrequency(frequency: number)</code> - Get detailed analysis including MIDI note, cents, etc.</li>
                    <li><code>isNearNote(frequency: number, targetNote: string, tolerance: number)</code> - Check if frequency matches note</li>
                </ul>

                <h3 class="text-h6 mt-6 mb-3">Usage Example</h3>
                <pre class="code-block"><code>// Create detector
const detector = createPitchDetector(DetectorType.BLUESHARP, 'flute');

// Get audio from microphone
const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
const audioContext = new AudioContext();
const source = audioContext.createMediaStreamSource(stream);
const analyser = audioContext.createAnalyser();
analyser.fftSize = 2048;
source.connect(analyser);

// Detect pitch
const buffer = new Float32Array(analyser.frequencyBinCount);
analyser.getFloatTimeDomainData(buffer);
const result = detector.detectPitch(buffer, audioContext.sampleRate);

if (result.frequency) {
  console.log(`${result.note} at ${result.frequency}Hz`);
}</code></pre>
            </v-card-text>
        </v-card>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from "vue";
import {
    createPitchDetector,
    DetectorType,
    type PitchDetectionResult,
} from "../pitch";

const isSupported = ref(!!navigator.mediaDevices?.getUserMedia);
const isActive = ref(false);
const error = ref<string | null>(null);
const currentResult = ref<PitchDetectionResult | null>(null);

const history = ref<Array<{ timestamp: string; result: PitchDetectionResult }>>(
    [],
);

const detector = createPitchDetector(DetectorType.BLUESHARP, "flute");
const detectorName = detector.getName();

let audioContext: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let animationFrameId: number | null = null;

const statusText = computed(() => {
    if (!isActive.value) return "Inactive";
    if (!currentResult.value?.frequency) return "Listening...";
    return "Detecting";
});

async function startDetection() {
    try {
        error.value = null;

        // Request microphone access
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false,
            },
        });

        // Create audio context
        audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(stream);

        // Create analyzer
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0.8;
        source.connect(analyser);

        isActive.value = true;

        // Start analysis loop
        analyze();

        console.log("[PitchDebug] Detection started");
    } catch (err) {
        error.value = `Failed to access microphone: ${err}`;
        console.error("[PitchDebug]", err);
    }
}

function stopDetection() {
    isActive.value = false;

    if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
    }

    if (audioContext) {
        audioContext.close();
        audioContext = null;
    }

    analyser = null;
    currentResult.value = null;

    console.log("[PitchDebug] Detection stopped");
}

function toggleDetection() {
    if (isActive.value) {
        stopDetection();
    } else {
        startDetection();
    }
}

let lastUpdateTime = 0;
const updateInterval = 50; // ms

function analyze(timestamp: number = 0) {
    if (!isActive.value || !analyser || !audioContext) return;

    // Throttle updates
    if (timestamp - lastUpdateTime < updateInterval) {
        animationFrameId = requestAnimationFrame(analyze);
        return;
    }
    lastUpdateTime = timestamp;

    // Get audio data
    const buffer = new Float32Array(analyser.frequencyBinCount);
    analyser.getFloatTimeDomainData(buffer);

    // Detect pitch
    const result = detector.detectPitch(buffer, audioContext.sampleRate);

    // Update current result
    currentResult.value = result;

    // Add to history if frequency detected
    if (result.frequency) {
        const timestamp = new Date().toLocaleTimeString();
        history.value.unshift({ timestamp, result });
        if (history.value.length > 10) {
            history.value.pop();
        }
    }

    // Continue loop
    animationFrameId = requestAnimationFrame(analyze);
}

onUnmounted(() => {
    stopDetection();
});
</script>

<style scoped>
.pitch-debug {
    padding: 20px;
    max-width: 800px;
    margin: 0 auto;
}

h1 {
    margin-bottom: 20px;
}

h3 {
    margin-top: 30px;
    margin-bottom: 10px;
}

.controls {
    margin-bottom: 20px;
}

button {
    padding: 10px 20px;
    font-size: 16px;
    cursor: pointer;
    /*background-color: blue;*/
}

button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.error {
    color: red;
    margin: 10px 0;
}

.status {
    margin: 20px 0;
    padding: 10px;
    border-radius: 4px;
}

.results {
    margin: 20px 0;
    padding: 20px;
    border: 0.5px solid #ddd;
    border-radius: 4px;
}

.result-item {
    margin: 10px 0;
    font-size: 18px;
}

.result-item strong {
    display: inline-block;
    width: 150px;
}

.raw-data {
    margin-top: 30px;
}

.raw-data pre {
    padding: 15px;
    border-radius: 4px;
    overflow-x: auto;
    font-size: 14px;
}

.history {
    margin-top: 30px;
}

.history-item {
    padding: 8px;
    margin: 5px 0;
    border-radius: 4px;
    font-family: monospace;
}

.timestamp {
    display: inline-block;
    width: 100px;
    color: #666;
    font-size: 12px;
}

.no-pitch {
    color: #999;
}

.api-section {
    margin-top: 40px;
}

.api-list {
    line-height: 1.8;
    padding-left: 20px;
}

.api-list li {
    margin-bottom: 8px;
}

.code-block {
    background: rgba(0, 0, 0, 0.05);
    padding: 12px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 8px 0;
}

.code-block code {
    font-family: "Fira Code", "Consolas", monospace;
    font-size: 13px;
    line-height: 1.5;
}
</style>
